import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip
} from '@mui/material';
import { Search as SearchIcon, Person, PhotoLibrary, LocationOn } from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import PostCard from '../components/Posts/PostCard';

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [tabValue, setTabValue] = useState(0);
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const searchQuery = searchParams.get('q');
    if (searchQuery) {
      setQuery(searchQuery);
      performSearch(searchQuery);
    }
  }, [searchParams]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;

    try {
      setLoading(true);
      setError('');

      const [postsResponse, usersResponse] = await Promise.all([
        axios.get(getApiUrl(`/api/posts/search/${encodeURIComponent(searchQuery)}`)),
        axios.get(getApiUrl(`/api/users/search/${encodeURIComponent(searchQuery)}`))
      ]);

      setPosts(postsResponse.data.posts);
      setUsers(usersResponse.data.users);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to perform search');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query.trim() });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleUserClick = (username) => {
    navigate(`/profile/${username}`);
  };

  const popularTags = [
    'beach', 'mountains', 'adventure', 'food', 'culture', 'solo', 'family', 'friends'
  ];

  return (
    <Container maxWidth="md" sx={{ mt: 4, pb: 4 }}>
      {/* Search Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Explore Trips
        </Typography>
        <form onSubmit={handleSearch}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search for trips, places, or people..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </form>

        {/* Popular Tags */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Popular Tags:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {popularTags.map((tag) => (
              <Chip
                key={tag}
                label={`#${tag}`}
                variant="outlined"
                size="small"
                onClick={() => {
                  setQuery(tag);
                  setSearchParams({ q: tag });
                }}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Search Results */}
      {query && (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab 
                icon={<PhotoLibrary />} 
                label={`Posts (${posts.length})`} 
                iconPosition="start"
              />
              <Tab 
                icon={<Person />} 
                label={`People (${users.length})`} 
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!loading && (
            <>
              {/* Posts Tab */}
              {tabValue === 0 && (
                <Grid container spacing={3}>
                  {posts.length === 0 ? (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No posts found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Try searching with different keywords
                        </Typography>
                      </Box>
                    </Grid>
                  ) : (
                    posts.map((post) => (
                      <Grid item xs={12} key={post._id}>
                        <PostCard post={post} />
                      </Grid>
                    ))
                  )}
                </Grid>
              )}

              {/* Users Tab */}
              {tabValue === 1 && (
                <Grid container spacing={2}>
                  {users.length === 0 ? (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No users found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Try searching with different keywords
                        </Typography>
                      </Box>
                    </Grid>
                  ) : (
                    users.map((user) => (
                      <Grid item xs={12} sm={6} md={4} key={user._id}>
                        <Card 
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 4 }
                          }}
                          onClick={() => handleUserClick(user.username)}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Avatar
                                src={user.profilePicture}
                                alt={user.fullName}
                                sx={{ width: 56, height: 56, mr: 2 }}
                              >
                                {user.fullName?.charAt(0)}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" component="div">
                                  {user.fullName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  @{user.username}
                                </Typography>
                              </Box>
                            </Box>
                            
                            {user.location && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <LocationOn sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {user.location}
                                </Typography>
                              </Box>
                            )}
                            
                            <Typography variant="body2" color="text.secondary">
                              {user.totalTrips} trips shared
                            </Typography>
                            
                            <Button
                              variant="outlined"
                              size="small"
                              sx={{ mt: 2 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUserClick(user.username);
                              }}
                            >
                              View Profile
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))
                  )}
                </Grid>
              )}
            </>
          )}
        </>
      )}

      {/* Default State */}
      {!query && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Discover Amazing Trips
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Search for destinations, experiences, or fellow travelers
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default Search;
